import React, { useEffect } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Button, Typography } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useStore } from 'zustand';

// eslint-disable-next-line no-restricted-imports
import { useUpdateRegionalCalendar } from 'features/regionalCalendar/update';
import { isHasAccess } from 'features/validateAccess';
import { calendarService, calendarStore, NATIONAL_CALENDAR_NAME } from 'entities/calendar';
import { ReactComponent as LeftArrowIcon } from 'shared/assets/icons/LongLeftArrow.svg';
import { withSuspense } from 'shared/lib/react';
import { pathKeys } from 'shared/lib/react-router';
import { LinkButton } from 'shared/ui/LinkButton';

import { UPDATE_CALENDAR } from '../../CalendarsLayout.permissions';
import { EventsList } from '../EventsList';
import { SkeletonLoader } from '../SkeletonLoader';
import styles from './CalendarSidebar.module.scss';

const today = dayjs();

const CalendarSidebar = (): React.ReactElement => {
  const { t } = useTranslation('Calendar');

  const { calendarId } = useParams();
  const selectedYear = useStore(calendarStore, (state) => state.selectedYear);
  const selectedMonth = useStore(calendarStore, (state) => state.selectedMonth);
  const selectedViewType = useStore(calendarStore, (state) => state.selectedViewType);
  const selectMonth = useStore(calendarStore, (state) => state.selectMonth);

  const { data: calendar } = useSuspenseQuery(
    calendarService.queryOptions({
      id: calendarId as string,
      year: selectedYear.toString(),
    }),
  );

  const openEditModal = useUpdateRegionalCalendar(calendar);

  useEffect(() => {
    return () => {
      selectMonth(today.month());
    };
  }, [selectMonth]);

  const filteredEvents =
    calendar?.events?.filter((event) => {
      const eventStart = dayjs(event.startDate);
      const eventEnd = dayjs(event.endDate);
      const matchesYear = eventStart.year() === selectedYear || eventEnd.year() === selectedYear;

      if (selectedViewType === 'month') {
        const monthStart = dayjs().year(selectedYear).month(selectedMonth).startOf('month');
        const monthEnd = monthStart.endOf('month');

        return (
          matchesYear &&
          (eventStart.isBetween(monthStart, monthEnd, 'day', '[]') ||
            eventEnd.isBetween(monthStart, monthEnd, 'day', '[]') ||
            (eventStart.isBefore(monthStart) && eventEnd.isAfter(monthEnd)))
        );
      }

      return matchesYear;
    }) || [];

  const canUpdateCalendar = isHasAccess(UPDATE_CALENDAR);

  return (
    <div className={styles.sidebarWrapper}>
      <LinkButton
        linkProps={{ to: pathKeys.calendars.root(), className: styles.backLink }}
        buttonProps={{ type: 'text', className: styles.backButton }}
      >
        <LeftArrowIcon />
        {t('calendarLayout.calendarSidebar.title')}
      </LinkButton>
      <div className={styles.contentWrapper}>
        <div className={styles.titleBlock}>
          {calendar.name !== NATIONAL_CALENDAR_NAME ? (
            <React.Fragment>
              <Typography.Title level={5} className={styles.calendarName}>
                {calendar?.name}
              </Typography.Title>
              {canUpdateCalendar && (
                <Button type="text" className={styles.editBtn} size="small" onClick={openEditModal}>
                  {t('calendarLayout.calendarSidebar.editBtn.text')}
                </Button>
              )}
            </React.Fragment>
          ) : (
            <Typography.Title level={5} className={styles.calendarName}>
              {t('calendarLayout.nationalCalendar.defaultName.text')}
            </Typography.Title>
          )}
        </div>
        {filteredEvents.length ? (
          <EventsList filteredEvents={filteredEvents} />
        ) : (
          <div className={styles.emptyList}>
            <Button type="text" className={styles.createBtn}>
              {t('calendarLayout.calendarSidebar.createBtn.text')}
            </Button>
          </div>
        )}
      </div>
      {/* Todo: Replace with actual last saved date from the calendar data */}
      <Typography.Text className={styles.lastSavedText}>
        {t('calendarLayout.calendarSidebar.lastUpdated.text', {
          date: dayjs(calendar?.updatedAt).format('DD MMM YYYY'),
          time: dayjs(calendar?.updatedAt).format('HH:mm'),
        })}
      </Typography.Text>
    </div>
  );
};

export default withSuspense(CalendarSidebar, { fallback: <SkeletonLoader /> });
